import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Film, Mic, Video, CheckCircle2, Circle } from 'lucide-react';
import type { TutorialStep } from '@platform/shared-types';
import ShimmerImage from '../../components/ShimmerImage';
import { stepClipSource } from '../../services/tutorialApi';

/**
 * Draws a still frame from a step's clip onto an offscreen canvas so it can
 * be handed to ShimmerImage as a plain `src` -- there is no per-step poster
 * from the render pipeline (only the WHOLE tutorial gets one, after render),
 * so this is the only way to show something before a step has ever been
 * rendered.
 *
 * A `slice` step's clip is a byte range of the WHOLE shared session
 * recording, not its own file -- `stepClipSource` already resolves that into
 * a URL + a seek offset, so this component only has to seek and grab a
 * frame.
 *
 * Best-effort: the media CDN may not send the CORS headers a canvas read
 * needs (`crossOrigin="anonymous"` without a matching
 * Access-Control-Allow-Origin taints the canvas and `toDataURL` throws
 * synchronously). That failure -- and any load failure -- falls through to
 * ShimmerImage's own fallback icon rather than breaking the row.
 */
function ClipThumbnail({ clip }: { clip: TutorialStep['clip'] }) {
  const [thumb, setThumb] = useState<string | null>(null);
  const source = stepClipSource(clip);

  useEffect(() => {
    setThumb(null);
    if (!source) return undefined;
    let cancelled = false;
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.preload = 'metadata';
    video.playsInline = true;

    const capture = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 160;
        canvas.height = video.videoHeight || 90;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        setThumb(canvas.toDataURL('image/jpeg', 0.7));
      } catch {
        // Tainted canvas (no CORS on the CDN response) or a decode failure --
        // leave `thumb` null so ShimmerImage renders its fallback icon.
      }
    };

    const onLoadedMetadata = () => {
      const target = Math.min(source.seekSeconds, Math.max(video.duration - 0.1, 0));
      // Setting currentTime when it's already ~0 (a standalone re-record)
      // never fires 'seeked' in some browsers -- capture directly instead.
      if (target <= 0.01) { capture(); return; }
      video.currentTime = target;
    };
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('seeked', capture);
    video.addEventListener('error', () => setThumb(null));
    video.src = source.url;

    return () => {
      cancelled = true;
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('seeked', capture);
      video.src = '';
    };
  }, [source?.url, source?.seekSeconds]);

  return (
    <ShimmerImage
      src={thumb}
      alt="Step clip preview"
      width={112}
      height={72}
      className="rounded-lg border border-gray-200 flex-shrink-0"
      objectFit="cover"
      fallbackIcon={<Film className="w-5 h-5 text-gray-400" />}
    />
  );
}

export interface StepReviewRowProps {
  step: TutorialStep;
  /** Human-facing position -- `order + 1`, matching describeBlockers. */
  displayNumber: number;
  /** True while this step's transcript may still be arriving (see the
   *  polling note on TutorialsTab) -- changes "no transcript" messaging from
   *  a flat negative into "still processing". */
  transcriptPending: boolean;
  onNarrationChange: (narration: string) => void;
  onToggleApproved: () => void;
  onReRecordAudio: () => void;
  onReRecordClip: () => void;
  busy?: boolean;
}

export default function StepReviewRow({
  step, displayNumber, transcriptPending,
  onNarrationChange, onToggleApproved, onReRecordAudio, onReRecordClip, busy,
}: StepReviewRowProps) {
  const [narrationDraft, setNarrationDraft] = useState(step.narration);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const lastSaved = useRef(step.narration);

  // Reset the local draft whenever the underlying step actually changes
  // (e.g. a background poll picked up a freshly-arrived transcript, or a
  // sibling step's edit forced a refetch) -- but not on every render, so an
  // in-progress keystroke is never clobbered by the component's own last save.
  useEffect(() => {
    if (step.narration !== lastSaved.current) {
      setNarrationDraft(step.narration);
      lastSaved.current = step.narration;
    }
  }, [step.narration]);

  const commitNarration = () => {
    if (narrationDraft === lastSaved.current) return;
    lastSaved.current = narrationDraft;
    onNarrationChange(narrationDraft);
  };

  const hasClip = !!step.clip;
  const hasTranscript = step.transcriptRaw.trim().length > 0;
  const hasNarration = step.narration.trim().length > 0;

  return (
    <div className="border border-gray-200 rounded-xl bg-white p-4 space-y-3">
      <div className="flex items-start gap-3">
        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-900 text-white text-xs font-bold flex items-center justify-center mt-1">
          {displayNumber}
        </span>

        <ClipThumbnail clip={step.clip} />

        <div className="flex-1 min-w-0 space-y-1">
          {/* What was clicked -- role, label and route, so the author can see
              why the narration says what it says. */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 font-semibold text-gray-700">
              {step.anchor.role || 'element'}
            </span>
            <span className="font-medium text-gray-700 truncate">{step.anchor.label || '(no label)'}</span>
            <span className="text-gray-300">·</span>
            <span className="font-mono truncate">{step.anchor.route}</span>
          </div>

          <textarea
            value={narrationDraft}
            onChange={(e) => setNarrationDraft(e.target.value)}
            onBlur={commitNarration}
            rows={2}
            placeholder={
              transcriptPending && !hasNarration
                ? 'Transcript still arriving — narration will appear once it does…'
                : 'Narration for this step'
            }
            disabled={busy}
            className="w-full min-h-[44px] rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-gray-900 focus:outline-none disabled:opacity-60"
          />

          <div className="flex items-center flex-wrap gap-2">
            <button
              type="button"
              onClick={onToggleApproved}
              disabled={busy || !hasNarration}
              aria-pressed={step.narrationApproved}
              className={`inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg text-xs font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed ${
                step.narrationApproved
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {step.narrationApproved ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
              {step.narrationApproved ? 'Approved' : 'Approve narration'}
            </button>

            <button
              type="button"
              onClick={onReRecordAudio}
              disabled={busy}
              className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg text-xs font-semibold text-gray-600 border border-gray-300 hover:border-gray-900 hover:text-gray-900 transition disabled:opacity-40"
            >
              <Mic className="w-3.5 h-3.5" />
              Re-record audio
            </button>

            <button
              type="button"
              onClick={onReRecordClip}
              disabled={busy}
              className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg text-xs font-semibold text-gray-600 border border-gray-300 hover:border-gray-900 hover:text-gray-900 transition disabled:opacity-40"
            >
              <Video className="w-3.5 h-3.5" />
              Re-record clip
            </button>
          </div>

          {!hasClip && (
            <p className="text-xs text-red-600 font-medium">No video clip recorded for this step yet.</p>
          )}
        </div>
      </div>

      {/* Raw transcript -- collapsed by default, but visible, so the author
          can tell a mishearing (fix the transcript's source) from a rewrite
          (the cleanup pass changed it on purpose). */}
      <div className="pl-10">
        <button
          type="button"
          onClick={() => setTranscriptOpen((v) => !v)}
          aria-expanded={transcriptOpen}
          className="inline-flex items-center gap-1 min-h-[32px] text-[11px] font-semibold uppercase tracking-wide text-gray-400 hover:text-gray-700 transition"
        >
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${transcriptOpen ? '' : '-rotate-90'}`} />
          Raw transcript
        </button>
        {transcriptOpen && (
          <p className="mt-1 text-xs text-gray-500 italic bg-gray-50 rounded-lg p-2 whitespace-pre-wrap">
            {hasTranscript
              ? step.transcriptRaw
              : transcriptPending
                ? 'Still arriving — this screen checks automatically.'
                : 'No transcript recorded for this step.'}
          </p>
        )}
      </div>
    </div>
  );
}
